import React from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Formik, ErrorMessage, Field } from 'formik';
import { Label, Col, FormGroup, Form, FormFeedback, Input, Row } from 'reactstrap';
import { toJS } from 'mobx';

import PrimaryButton from '../PrimaryButton';

import '../FormComponents/styles.scss';
import Spinner from '../Spinner';
import Toggle from '../Toggle';
import '../../pages/ProfilePage/styles.scss';
import CustomSelect from '../CustomSelect';

import './styles.scss';
import { isButtonForNewslettersSubscribeDisabled } from '../../utils/helpers';
import InfoMessage from '../InfoMessage';
import { userNewsStatus } from '../../utils/constants';
import { useStore } from '../../store';

const CustomFormFeedback = ({ children }) => <FormFeedback className="styled-form-feedback">{children}</FormFeedback>;

const NewslettersForm = observer(({ interests, language }) => {
  const {
    rootStore: {
      profileStore,
      profileStore: { newsletterStore },
    },
  } = useStore();

  const { t } = useTranslation('account');

  const initialData = toJS(profileStore.profileData);

  if (!initialData) {
    return null;
  }

  let initialValues = {
    marketingUpdates: profileStore.profileData.marketingUpdates,
    language: language || initialData.language,
    interests: interests,
  };

  if (initialValues.language === 'en') {
    initialValues.language = 'de';
  }

  return (
    <>
      <Formik
        initialValues={initialValues}
        validate={() => true}
        onSubmit={(values, { setSubmitting }) => {
          newsletterStore.savePreferences(values, setSubmitting, t);
        }}
      >
        {({ values, errors, touched, setFieldValue, setFieldTouched, handleSubmit, isSubmitting }) => {
          const allUnsubscribed = values.interests.every(item => !item.interested);

          const isButtonDisabled = isButtonForNewslettersSubscribeDisabled(
            isSubmitting,
            values,
            errors,
            newsletterStore.userSubscriptionStatus
          );

          return (
            <Form className="styled-form" onSubmit={handleSubmit}>
              {values.interests ? (
                values.interests.map((item, idx) => (
                  <Row className="styled-subscriptions-row" key={item.id}>
                    <Col xs="8" sm="9">
                      {item.name}
                    </Col>
                    <Col xs="4" sm="3" className="text-right">
                      <Toggle
                        name={`interests[${idx}].interested`}
                        value={item.interested}
                        onChange={() => {
                          if (allUnsubscribed) {
                            setFieldValue('marketingUpdates', false);
                            setFieldTouched('marketingUpdates', true);
                          }
                          setFieldValue(`interests[${idx}].interested`, !item.interested);
                          setFieldTouched(`interests[${idx}].interested`, true);
                        }}
                      />
                    </Col>
                  </Row>
                ))
              ) : (
                <Spinner />
              )}
              <FormGroup>
                <Label className={`profile-select-label ${!!errors.langage && touched.language ? 'is-invalid' : ''}`}>
                  {t('language.label')}
                </Label>
                <Field
                  className="styled-field"
                  component={CustomSelect}
                  isSearchable={false}
                  name="language"
                  isClearable={false}
                  isDisabled={allUnsubscribed}
                  options={[
                    {
                      label: t('language.values.de'),
                      value: 'de',
                    },
                    {
                      label: t('language.values.fr'),
                      value: 'fr',
                    },
                    {
                      label: t('language.values.it'),
                      value: 'it',
                    },
                    // CWI-203 English is not supported in mailing
                    // {
                    //   label: t('language.values.en'),
                    //   value: 'en',
                    // },
                  ]}
                  placeholder={t('language.placeholder')}
                />
                <ErrorMessage name="language" component={CustomFormFeedback} />
              </FormGroup>
              <FormGroup className="terms-form-group" check>
                <Label check className="cursor-pointer">
                  <Input
                    type="checkbox"
                    className="checkbox cursor-pointer styled-input-checkbox"
                    name="marketingUpdates"
                    value={values.marketingUpdates}
                    onChange={ev => {
                      setFieldValue('marketingUpdates', ev.target.checked);
                      setFieldTouched('marketingUpdates', true);
                    }}
                    checked={allUnsubscribed ? false : values.marketingUpdates}
                  />
                  <p>
                    <span
                      dangerouslySetInnerHTML={{
                        __html: t('marketing.label'),
                      }}
                    />
                  </p>
                </Label>
              </FormGroup>
              <PrimaryButton type="submit" disabled={isButtonDisabled || Object.keys(touched).length === 0}>
                {t('saveButton')}
              </PrimaryButton>
              {newsletterStore.userSubscriptionStatus === userNewsStatus.PENDING && (
                <InfoMessage text={t('newsletters.subscriptionStatusInfo')} />
              )}
            </Form>
          );
        }}
      </Formik>
    </>
  );
});

export default NewslettersForm;
